// Admin Review → Mass Email (2026-10-23, v2.3.1). Pick clients (and/or MIDTC staff), say
// what kind of email it is — Informational, New feature, Security or Server/service
// maintenance — and the matching template opens with the fields that kind needs, plus a
// live preview of the real email. Every person gets their own copy from MIDTC Support.
// Past sends are logged below with each recipient's outcome. Backend: src/massEmail.js.
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import API from "../api";
import { Mail, Info, Sparkles, ShieldAlert, Wrench, Users, Building2, Search, X, Check, ChevronLeft, Send, Image as ImageIcon,
         RefreshCw, Plus, Trash2, AlertTriangle, Clock, FlaskConical } from "lucide-react";
import { ConvoStyles, Sel } from "./conversation/Conversation";

const TYPES = {
  informational: { label: "Informational",              short: "Information",    color: "#2563eb", Icon: Info,
    hint: "General news: office hours, policy changes, a heads-up about something coming." },
  feature:       { label: "New feature",                short: "New feature",    color: "#7c3aed", Icon: Sparkles,
    hint: "Announce something new or improved, with highlights and screenshots." },
  security:      { label: "Security related",           short: "Security",       color: "#dc2626", Icon: ShieldAlert,
    hint: "A threat, a breach, or steps people must take to stay safe." },
  maintenance:   { label: "Server or service maintenance", short: "Maintenance", color: "#d97706", Icon: Wrench,
    hint: "Planned work with a start and end time and what will be affected." },
};
const REASON_LABEL = {
  informational: "Reason for the message", feature: "Why we built it (reason for the message)",
  security: "Why this matters (reason for the message)", maintenance: "Why we're doing the maintenance (reason)",
};
const STATUS = {
  queued: { t: "Queued", c: "#d97706" }, sending: { t: "Sending", c: "#2563eb" }, sent: { t: "Sent", c: "#16a34a" },
  partial: { t: "Sent — some failed", c: "#ea580c" }, failed: { t: "Failed", c: "#dc2626" }, cancelled: { t: "Cancelled", c: "#64748b" },
  skipped: { t: "Not sent", c: "#64748b" },
};
const EMPTY = {
  subject: "", headline: "", summary: "", details: "", reason: "", cta_label: "", cta_url: "",
  version: "", highlights: [{ title: "", text: "", image_id: "" }], steps: "",
  severity: "", affected: "", no_action: false, actions: "", deadline: "", our_actions: "",
  start: "", end: "", services: "", impact: "",
};
const fmtET = (d) => (d ? new Date(d).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) + " ET" : "—");

const pill = (c, filled) => ({ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 999,
  color: filled ? "#fff" : c, background: filled ? c : `color-mix(in srgb, ${c} 14%, transparent)`, border: `1px solid color-mix(in srgb, ${c} 40%, transparent)`, whiteSpace: "nowrap" });
const panelStyle = { background: "var(--t-gloss, none), var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: "var(--t-radius-lg, 16px)", boxShadow: "var(--t-shadow-panel)" };
const lbl = { display: "block", fontSize: 12, fontWeight: 700, color: "var(--t-text-label)", margin: "0 0 6px" };
const inp = { width: "100%", height: 38, padding: "0 12px", fontSize: 13.5, borderRadius: "var(--t-radius-sm, 8px)" };
const area = { width: "100%", padding: "10px 12px", fontSize: 13.5, lineHeight: 1.55, resize: "vertical", borderRadius: "var(--t-radius-sm, 8px)" };

function Field({ label, hint, children, error }) {
  return (
    <label style={{ display: "block", marginBottom: 14 }}>
      <span style={lbl}>{label}</span>
      {children}
      {hint && <span style={{ display: "block", fontSize: 11.5, color: "var(--t-text-faint)", marginTop: 5, lineHeight: 1.45 }}>{hint}</span>}
      {error && <span style={{ display: "block", fontSize: 12, color: "var(--badge-red)", marginTop: 5 }}>{error}</span>}
    </label>
  );
}

function Modal({ title, onClose, children, width = 720 }) {
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div role="dialog" aria-modal="true" aria-label={title} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(2,6,23,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ ...panelStyle, background: "var(--t-panel)", width: "100%", maxWidth: width, maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid var(--t-border-softer)" }}>
          <h2 style={{ fontSize: 17, margin: 0 }}>{title}</h2>
          <button type="button" className="cv-btn cv-btn-icon cv-btn-ghost" aria-label="Close" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

// ── The log ──────────────────────────────────────────────────────────────────
function Log({ onNew, highlight }) {
  const [rows, setRows] = useState(null);
  const [queue, setQueue] = useState(0);
  const [perMin, setPerMin] = useState(24);
  const [open, setOpen] = useState(null);
  const load = useCallback(async () => {
    try { const r = await API.get("/admin/mass-emails"); setRows(r.data.campaigns || []); setQueue(r.data.queue || 0); setPerMin(r.data.per_minute || 24); }
    catch { setRows((p) => p || []); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const live = (rows || []).some((r) => ["queued", "sending"].includes(r.status));
  useEffect(() => { if (!live) return; const t = setInterval(load, 4000); return () => clearInterval(t); }, [live, load]);
  useEffect(() => { if (highlight) setOpen(highlight); }, [highlight]);

  return (
    <div style={{ ...panelStyle, padding: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "16px 20px", borderBottom: "1px solid var(--t-border-softer)" }}>
        <Clock size={16} style={{ color: "var(--t-text-faint)" }} />
        <h2 style={{ fontSize: 15, margin: 0 }}>Sent mass emails</h2>
        {queue > 0 && <span style={pill("#2563eb")}>{queue} waiting to send · about {Math.ceil(queue / perMin)} min</span>}
        <div style={{ flex: 1 }} />
        <button type="button" className="cv-btn" onClick={load}><RefreshCw size={14} /> Refresh</button>
        <button type="button" className="cv-btn cv-btn-primary" onClick={onNew}><Plus size={15} /> New mass email</button>
      </div>
      {rows === null ? <div style={{ padding: 30, textAlign: "center", color: "var(--t-text-faint)" }}>Loading…</div>
        : rows.length === 0 ? <div style={{ padding: 36, textAlign: "center", color: "var(--t-text-faint)", fontSize: 13.5 }}>No mass emails yet. Click <strong>New mass email</strong> to send the first one.</div>
        : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
              <thead><tr>{["Sent", "Kind", "Subject", "To", "Delivered", "Status", "By"].map((h) => (
                <th key={h} style={{ textAlign: "left", fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--t-text-faint)", padding: "10px 14px", borderBottom: "1px solid var(--t-border-softer)" }}>{h}</th>))}</tr></thead>
              <tbody>
                {rows.map((r) => {
                  const T = TYPES[r.type] || TYPES.informational, S = STATUS[r.status] || STATUS.queued;
                  const to = [...(r.client_names || []).slice(0, 2), ...(r.include_internal ? ["Internal staff"] : [])].join(", ") + ((r.client_names || []).length > 2 ? ` +${r.client_names.length - 2} more` : "");
                  const done = r.sent + r.failed + r.skipped + r.cancelled;
                  return (
                    <tr key={r.id} onClick={() => setOpen(r.id)} style={{ cursor: "pointer", borderBottom: "1px solid var(--t-border-softer)", background: r.id === highlight ? "var(--t-accent-softer)" : "transparent" }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "var(--t-accent-softer)"; }} onMouseLeave={(e) => { e.currentTarget.style.background = r.id === highlight ? "var(--t-accent-softer)" : "transparent"; }}>
                      <td style={{ padding: "11px 14px", fontSize: 12.5, color: "var(--t-text-muted)", whiteSpace: "nowrap" }}>{fmtET(r.created_at)}</td>
                      <td style={{ padding: "11px 14px" }}><span style={pill(T.color)}><T.Icon size={11} /> {T.short}</span></td>
                      <td style={{ padding: "11px 14px", fontSize: 13.5, fontWeight: 600, color: "var(--t-text-primary)", maxWidth: 320 }}>
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.subject}</div></td>
                      <td style={{ padding: "11px 14px", fontSize: 12.5, color: "var(--t-text-muted)", maxWidth: 220 }}><div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{to}</div></td>
                      <td style={{ padding: "11px 14px", fontSize: 12.5, whiteSpace: "nowrap", color: "var(--t-text-body)" }}>
                        {r.sent}/{r.total}{r.failed ? <span style={{ color: "var(--badge-red)" }}> · {r.failed} failed</span> : null}
                        {["queued", "sending"].includes(r.status) && <div style={{ height: 4, borderRadius: 99, background: "var(--t-surface-mid)", marginTop: 5, width: 110 }}>
                          <div style={{ height: "100%", width: `${r.total ? Math.round((done / r.total) * 100) : 0}%`, borderRadius: 99, background: "var(--t-accent-grad)" }} /></div>}
                      </td>
                      <td style={{ padding: "11px 14px" }}><span style={pill(S.c)}>{S.t}</span></td>
                      <td style={{ padding: "11px 14px", fontSize: 12.5, color: "var(--t-text-muted)", whiteSpace: "nowrap" }}>{r.created_by_name}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      {open && <Detail id={open} onClose={() => { setOpen(null); load(); }} />}
    </div>
  );
}

function Detail({ id, onClose }) {
  const [d, setD] = useState(null);
  const [f, setF] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { try { setD((await API.get(`/admin/mass-emails/${id}`)).data); } catch { setD({ error: true }); } }, [id]);
  useEffect(() => { load(); }, [load]);
  const live = d && ["queued", "sending"].includes(d.status);
  useEffect(() => { if (!live) return; const t = setInterval(load, 4000); return () => clearInterval(t); }, [live, load]);
  const act = async (path) => { setBusy(true); try { await API.post(`/admin/mass-emails/${id}/${path}`); await load(); } finally { setBusy(false); } };
  const list = useMemo(() => (d?.recipients || []).filter((r) => (f === "all" || r.status === f) && (!q || `${r.name} ${r.email} ${r.client_name || ""}`.toLowerCase().includes(q.toLowerCase()))), [d, f, q]);
  if (!d) return <Modal title="Loading…" onClose={onClose}><div style={{ color: "var(--t-text-faint)" }}>Loading…</div></Modal>;
  if (d.error) return <Modal title="Couldn't load" onClose={onClose}><div>That email couldn't be loaded.</div></Modal>;
  const T = TYPES[d.type] || TYPES.informational, S = STATUS[d.status] || STATUS.queued;
  const counts = (d.recipients || []).reduce((o, r) => ({ ...o, [r.status]: (o[r.status] || 0) + 1 }), {});
  return (
    <Modal title={d.subject} onClose={onClose} width={1100}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        <span style={pill(T.color)}><T.Icon size={11} /> {T.short}</span>
        <span style={pill(S.c)}>{S.t}</span>
        <span style={{ fontSize: 12.5, color: "var(--t-text-muted)" }}>Queued {fmtET(d.created_at)} by {d.created_by_name}{d.finished_at ? ` · finished ${fmtET(d.finished_at)}` : ""}</span>
        <div style={{ flex: 1 }} />
        {live && <button type="button" className="cv-btn cv-btn-danger" disabled={busy} onClick={() => window.confirm("Stop sending? People who already got it keep it.") && act("cancel")}><X size={14} /> Stop sending</button>}
        {!live && d.failed > 0 && <button type="button" className="cv-btn" disabled={busy} onClick={() => act("retry")}><RefreshCw size={14} /> Retry the {d.failed} failed</button>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 16 }} className="me-grid">
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            {["all", "sent", "failed", "queued", "skipped", "cancelled"].filter((k) => k === "all" || counts[k]).map((k) => (
              <button key={k} type="button" className={`cv-chip${f === k ? " on" : ""}`} style={{ height: 30, fontSize: 12 }} onClick={() => setF(k)}>
                {k === "all" ? "Everyone" : (STATUS[k] || { t: k }).t}<span className="cv-count">{k === "all" ? d.recipients.length : counts[k]}</span></button>))}
          </div>
          <label className="cv-input" style={{ display: "flex", alignItems: "center", gap: 8, height: 34, padding: "0 10px", marginBottom: 8 }}>
            <Search size={14} style={{ color: "var(--t-text-faint)" }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a person or company" aria-label="Find a recipient"
              style={{ border: 0, background: "transparent", color: "inherit", fontFamily: "inherit", fontSize: 13, outline: "none", width: "100%" }} />
          </label>
          <div style={{ border: "1px solid var(--t-border-softer)", borderRadius: 10, maxHeight: 460, overflowY: "auto" }}>
            {list.map((r) => {
              const s = { sent: "#16a34a", failed: "#dc2626", queued: "#d97706", sending: "#2563eb", skipped: "#64748b", cancelled: "#64748b" }[r.status] || "#64748b";
              return (
                <div key={r.id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 12px", borderBottom: "1px solid var(--t-border-softer)", fontSize: 13 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: "var(--t-text-primary)" }}>{r.name}</div>
                    <div style={{ fontSize: 12, color: "var(--t-text-faint)", overflow: "hidden", textOverflow: "ellipsis" }}>{r.email}{r.client_name ? ` · ${r.client_name}` : " · MIDTC staff"}</div>
                    {r.error && r.status !== "sent" && <div style={{ fontSize: 11.5, color: "var(--badge-red)", marginTop: 2 }}>{r.error}</div>}
                  </div>
                  <span style={pill(s)}>{(STATUS[r.status] || { t: r.status }).t}</span>
                </div>
              );
            })}
            {!list.length && <div style={{ padding: 16, fontSize: 13, color: "var(--t-text-faint)" }}>Nobody matches.</div>}
          </div>
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--t-text-label)", marginBottom: 8 }}>What they received</div>
          <iframe title="Email as sent" srcDoc={d.html} sandbox="" style={{ width: "100%", height: 560, border: "1px solid var(--t-border-softer)", borderRadius: 10, background: "#eef2f7" }} />
        </div>
      </div>
      <style>{`@media (max-width: 900px){ .me-grid{ grid-template-columns: 1fr !important; } }`}</style>
    </Modal>
  );
}

// ── Compose ──────────────────────────────────────────────────────────────────
function Compose({ onCancel, onQueued }) {
  const [audience, setAudience] = useState(null);
  const [clientIds, setClientIds] = useState(() => new Set());
  const [internal, setInternal] = useState(false);
  const [search, setSearch] = useState("");
  const [step, setStep] = useState(1);              // 1 who · 2 write · 3 review
  const [typeOpen, setTypeOpen] = useState(false);
  const [type, setType] = useState("");
  const [draftReason, setDraftReason] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [images, setImages] = useState([]);         // {id, name, caption, url}
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState({ html: "", errors: [] });
  const [people, setPeople] = useState(null);       // {groups, internal}
  const [chosen, setChosen] = useState(() => new Set());
  const [sending, setSending] = useState(false);
  const [testState, setTestState] = useState("");
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  useEffect(() => { API.get("/admin/mass-email/audience").then((r) => setAudience(r.data)).catch(() => setErr("The client list couldn't be loaded.")); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const T = TYPES[type];

  // What the server needs, from the form.
  const payload = useMemo(() => {
    const lines = (s) => String(s || "").split("\n").map((x) => x.trim()).filter(Boolean);
    const f = { headline: form.headline, summary: form.summary, details: form.details, reason: form.reason, cta_label: form.cta_label, cta_url: form.cta_url };
    if (type === "feature") Object.assign(f, { version: form.version, highlights: form.highlights, steps: lines(form.steps) });
    if (type === "security") Object.assign(f, { severity: form.severity, affected: form.affected, no_action: form.no_action, actions: lines(form.actions), deadline: form.deadline, our_actions: form.our_actions });
    if (type === "maintenance") Object.assign(f, { start: form.start, end: form.end, services: form.services, impact: form.impact, actions: lines(form.actions) });
    return { type, subject: form.subject || form.headline, fields: f, images: images.map(({ id, caption }) => ({ id, caption })) };
  }, [type, form, images]);

  // Live preview of the real email (server renders it, so it is exactly what is sent).
  useEffect(() => {
    if (step !== 2 || !type) return;
    const t = setTimeout(() => {
      API.post("/admin/mass-email/preview", { ...payload, sample_name: "Dana", sample_kind: internal && !clientIds.size ? "staff" : "contact" })
        .then((r) => setPreview({ html: r.data.html, errors: r.data.errors || [] }))
        .catch((e) => setPreview((p) => ({ ...p, errors: e.response?.data?.errors || ["The preview couldn't be built."] })));
    }, 500);
    return () => clearTimeout(t);
  }, [payload, step, type, internal, clientIds]);

  const pickType = (k) => {
    setType(k);
    setForm((f) => ({ ...f, reason: f.reason || draftReason }));
  };
  const startWriting = () => {
    if (!type || !draftReason.trim()) return;
    setForm((f) => ({ ...f, reason: draftReason }));
    setTypeOpen(false); setStep(2);
  };

  const addImages = async (files) => {
    if (!files.length) return;
    setUploading(true); setErr("");
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("images", f));
      const r = await API.post("/admin/mass-email/images", fd, { headers: { "Content-Type": undefined } });
      const urls = files.map((f) => URL.createObjectURL(f));
      setImages((cur) => [...cur, ...r.data.images.map((im, i) => ({ id: im.id, name: im.name, caption: "", url: urls[i] }))]);
    } catch (e) { setErr(e.response?.data?.error || "The picture couldn't be added."); }
    finally { setUploading(false); }
  };

  const goReview = async () => {
    setErr("");
    if (preview.errors.length) { setErr(preview.errors.join(" ")); return; }
    try {
      const r = await API.post("/admin/mass-email/recipients", { client_ids: [...clientIds], include_internal: internal });
      setPeople(r.data);
      const all = [...r.data.groups.flatMap((g) => g.people), ...r.data.internal];
      setChosen(new Set(all.filter((p) => !p.role && !p.blocked).map((p) => p.email)));
      setStep(3);
    } catch { setErr("The recipient list couldn't be loaded."); }
  };

  const sendTest = async () => {
    setTestState("sending");
    try { const r = await API.post("/admin/mass-email/test", payload); setTestState(`Test sent to ${r.data.to}`); }
    catch (e) { setTestState(e.response?.data?.errors?.join(" ") || e.response?.data?.error || "The test didn't go out."); }
  };

  const send = async () => {
    const n = chosen.size;
    if (!window.confirm(`Send this ${T.short.toLowerCase()} email to ${n} ${n === 1 ? "person" : "people"}? Each gets their own copy from MIDTC Support. This can be stopped while it's sending, but not undone.`)) return;
    setSending(true); setErr("");
    try {
      const r = await API.post("/admin/mass-emails", { ...payload, client_ids: [...clientIds], include_internal: internal, selected_emails: [...chosen] });
      onQueued(r.data.id);
    } catch (e) { setErr(e.response?.data?.errors?.join(" ") || e.response?.data?.error || "It couldn't be queued."); }
    finally { setSending(false); }
  };

  const clients = (audience?.clients || []).filter((c) => c.people + c.shared > 0 && (!search || c.name.toLowerCase().includes(search.toLowerCase())));
  const toggleClient = (id) => setClientIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectedPeople = (audience?.clients || []).filter((c) => clientIds.has(c.id)).reduce((n, c) => n + c.people, 0) + (internal ? audience?.internal?.people || 0 : 0);

  const steps = ["Who gets it", "Write it", "Check and send"];
  return (
    <div style={{ ...panelStyle, padding: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "14px 20px", borderBottom: "1px solid var(--t-border-softer)" }}>
        <button type="button" className="cv-btn cv-btn-ghost" onClick={step === 1 ? onCancel : () => setStep(step - 1)}><ChevronLeft size={15} /> {step === 1 ? "Back to the log" : "Back"}</button>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {steps.map((s, i) => <span key={s} style={pill(i + 1 === step ? "var(--t-accent)" : "#94a3b8", i + 1 === step)}>{i + 1}. {s}</span>)}
        </div>
        {T && step > 1 && <span style={{ ...pill(T.color), marginLeft: "auto" }}><T.Icon size={11} /> {T.label}</span>}
      </div>

      {err && <div role="alert" style={{ margin: "14px 20px 0", padding: "10px 14px", borderRadius: 10, border: "1px solid rgba(239,68,68,.35)", background: "rgba(239,68,68,.08)", color: "var(--badge-red)", fontSize: 13 }}>{err}</div>}

      {step === 1 && (
        <div style={{ padding: 20 }}>
          <button type="button" onClick={() => setInternal((v) => !v)} aria-pressed={internal}
            style={{ width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", marginBottom: 16, cursor: "pointer", fontFamily: "inherit",
              borderRadius: 12, border: `1px solid ${internal ? "var(--t-accent)" : "var(--t-border-soft)"}`, background: internal ? "var(--t-accent-softer)" : "var(--t-input)", color: "inherit" }}>
            <span style={{ width: 20, height: 20, borderRadius: 6, border: `2px solid ${internal ? "var(--t-accent)" : "var(--t-border-strong)"}`, background: internal ? "var(--t-accent)" : "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {internal && <Check size={13} color="#fff" />}</span>
            <Users size={20} style={{ color: "var(--t-accent)", flexShrink: 0 }} />
            <span style={{ flex: 1 }}>
              <strong style={{ display: "block", color: "var(--t-text-primary)", fontSize: 14 }}>Internal staff</strong>
              <span style={{ fontSize: 12.5, color: "var(--t-text-muted)" }}>Everyone at MIDTC — {audience ? `${audience.internal.people} people` : "…"}{audience?.internal?.shared ? ` (${audience.internal.shared} shared or system mailboxes left out)` : ""}</span>
            </span>
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
            <strong style={{ fontSize: 14, color: "var(--t-text-primary)" }}>Clients</strong>
            <span style={{ fontSize: 12.5, color: "var(--t-text-faint)" }}>Each person at a client you tick gets their own email.</span>
            <div style={{ flex: 1 }} />
            <button type="button" className="cv-link" onClick={() => setClientIds(new Set(clients.map((c) => c.id)))}>Select all</button>
            <button type="button" className="cv-link" onClick={() => setClientIds(new Set())}>Clear</button>
          </div>
          <label className="cv-input" style={{ display: "flex", alignItems: "center", gap: 8, height: 38, padding: "0 12px", marginBottom: 10 }}>
            <Search size={15} style={{ color: "var(--t-text-faint)" }} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find a client" aria-label="Find a client"
              style={{ border: 0, background: "transparent", color: "inherit", fontFamily: "inherit", fontSize: 13.5, outline: "none", width: "100%" }} />
          </label>
          <div role="listbox" aria-multiselectable="true" aria-label="Clients" style={{ border: "1px solid var(--t-border-soft)", borderRadius: 12, maxHeight: 360, overflowY: "auto" }}>
            {!audience && <div style={{ padding: 16, color: "var(--t-text-faint)" }}>Loading clients…</div>}
            {clients.map((c) => {
              const on = clientIds.has(c.id);
              return (
                <div key={c.id} role="option" aria-selected={on} tabIndex={0} onClick={() => toggleClient(c.id)} onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); toggleClient(c.id); } }}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderBottom: "1px solid var(--t-border-softer)", cursor: "pointer", background: on ? "var(--t-accent-softer)" : "transparent" }}>
                  <span style={{ width: 18, height: 18, borderRadius: 5, border: `2px solid ${on ? "var(--t-accent)" : "var(--t-border-strong)"}`, background: on ? "var(--t-accent)" : "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {on && <Check size={12} color="#fff" />}</span>
                  <Building2 size={16} style={{ color: "var(--t-text-faint)", flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: "var(--t-text-primary)" }}>{c.name}</span>
                  <span style={{ fontSize: 12.5, color: "var(--t-text-muted)", whiteSpace: "nowrap" }}>{c.people} {c.people === 1 ? "person" : "people"}{c.shared ? ` · ${c.shared} shared` : ""}</span>
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: "var(--t-text-muted)" }}>{clientIds.size} client{clientIds.size === 1 ? "" : "s"}{internal ? " + internal staff" : ""} · about {selectedPeople} people</span>
            <div style={{ flex: 1 }} />
            <button type="button" className="cv-btn cv-btn-primary" disabled={!clientIds.size && !internal} onClick={() => setTypeOpen(true)}>Next <Send size={14} /></button>
          </div>
        </div>
      )}

      {typeOpen && (
        <Modal title="What kind of email is this?" onClose={() => setTypeOpen(false)}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 16 }}>
            {Object.entries(TYPES).map(([k, t]) => {
              const on = type === k;
              return (
                <button key={k} type="button" onClick={() => pickType(k)} aria-pressed={on}
                  style={{ textAlign: "left", padding: 16, borderRadius: 14, cursor: "pointer", fontFamily: "inherit", color: "inherit",
                    border: `2px solid ${on ? t.color : "var(--t-border-soft)"}`, background: on ? `color-mix(in srgb, ${t.color} 10%, var(--t-panel))` : "var(--t-input)" }}>
                  <span style={{ display: "inline-flex", width: 36, height: 36, borderRadius: 10, alignItems: "center", justifyContent: "center", background: `color-mix(in srgb, ${t.color} 16%, transparent)`, color: t.color, marginBottom: 10 }}><t.Icon size={19} /></span>
                  <strong style={{ display: "block", fontSize: 14.5, color: "var(--t-text-primary)", marginBottom: 4 }}>{t.label}</strong>
                  <span style={{ fontSize: 12.5, color: "var(--t-text-muted)", lineHeight: 1.45 }}>{t.hint}</span>
                </button>
              );
            })}
          </div>
          <Field label="Reason for the message" hint="One or two sentences — it appears in the email so people know why they're getting it.">
            <textarea className="cv-input" rows={3} value={draftReason} onChange={(e) => setDraftReason(e.target.value)} style={area}
              placeholder={type === "maintenance" ? "e.g. We're installing security updates and replacing a failing disk." : type === "security" ? "e.g. Resetting now stops anyone who may have guessed an old password." : "e.g. You asked for an easier way to follow long tickets."} />
          </Field>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button type="button" className="cv-btn" onClick={() => setTypeOpen(false)}>Cancel</button>
            <button type="button" className="cv-btn cv-btn-primary" disabled={!type || !draftReason.trim()} onClick={startWriting}>Use this template</button>
          </div>
        </Modal>
      )}

      {step === 2 && T && (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.05fr)", gap: 0 }} className="me-grid">
          <div style={{ padding: 20, borderRight: "1px solid var(--t-border-softer)", minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <span style={{ display: "inline-flex", width: 32, height: 32, borderRadius: 9, alignItems: "center", justifyContent: "center", background: `color-mix(in srgb, ${T.color} 16%, transparent)`, color: T.color }}><T.Icon size={17} /></span>
              <strong style={{ fontSize: 15, color: "var(--t-text-primary)" }}>{T.label} email</strong>
              <button type="button" className="cv-link" style={{ marginLeft: "auto" }} onClick={() => { setDraftReason(form.reason); setTypeOpen(true); }}>Change kind</button>
            </div>
            <Field label="Subject line"><input className="cv-input" style={inp} value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder={form.headline || "What people see in their inbox"} /></Field>
            <Field label="Headline"><input className="cv-input" style={inp} value={form.headline} onChange={(e) => set("headline", e.target.value)} placeholder={type === "maintenance" ? "Email and file server maintenance on Saturday" : type === "feature" ? "A faster, cleaner ticket conversation" : "The big line at the top of the email"} /></Field>
            <Field label="Summary (one line under the headline)"><input className="cv-input" style={inp} value={form.summary} onChange={(e) => set("summary", e.target.value)} /></Field>

            {type === "maintenance" && (<>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="Starts (Eastern time)"><input type="datetime-local" className="cv-input" style={inp} value={form.start} onChange={(e) => set("start", e.target.value)} /></Field>
                <Field label="Ends (Eastern time)"><input type="datetime-local" className="cv-input" style={inp} value={form.end} onChange={(e) => set("end", e.target.value)} /></Field>
              </div>
              <Field label="Services affected"><input className="cv-input" style={inp} value={form.services} onChange={(e) => set("services", e.target.value)} placeholder="e.g. Email, shared drives (S: and T:), VPN" /></Field>
              <Field label="Expected impact">
                <Sel label="Expected impact" value={form.impact} onChange={(e) => set("impact", e.target.value)}>
                  <option value="">Choose…</option>
                  <option value="outage">Full outage — unavailable during the window</option>
                  <option value="degraded">Partial — some features slower or unavailable</option>
                  <option value="brief">Brief interruptions of a few minutes</option>
                  <option value="none">No interruption expected</option>
                </Sel>
              </Field>
            </>)}

            {type === "security" && (<>
              <Field label="How serious is it?">
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {[["critical", "Critical — action required", "#dc2626"], ["important", "Important", "#ea580c"], ["advisory", "Advisory — for information", "#2563eb"]].map(([k, t, c]) => (
                    <button key={k} type="button" className={`cv-chip${form.severity === k ? " on" : ""}`} onClick={() => set("severity", k)}
                      style={form.severity === k ? { borderColor: c, background: `color-mix(in srgb, ${c} 14%, transparent)` } : undefined}>{t}</button>))}
                </div>
              </Field>
              <Field label="Who is affected"><input className="cv-input" style={inp} value={form.affected} onChange={(e) => set("affected", e.target.value)} placeholder="e.g. Everyone at Harbor Ledger" /></Field>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, marginBottom: 12, cursor: "pointer" }}>
                <input type="checkbox" checked={form.no_action} onChange={(e) => set("no_action", e.target.checked)} /> No action needed from them
              </label>
              {!form.no_action && <Field label="What they need to do (one step per line)"><textarea className="cv-input" rows={4} style={area} value={form.actions} onChange={(e) => set("actions", e.target.value)} placeholder={"Go to https://aka.ms/sspr\nChoose a new password\nApprove the Authenticator prompt"} /></Field>}
              <Field label="Complete by (optional)"><input type="date" className="cv-input" style={inp} value={form.deadline} onChange={(e) => set("deadline", e.target.value)} /></Field>
            </>)}

            {type === "feature" && (
              <Field label="Version (optional)"><input className="cv-input" style={inp} value={form.version} onChange={(e) => set("version", e.target.value)} placeholder="e.g. 2.3.1" /></Field>
            )}

            <Field label={type === "maintenance" ? "What to expect" : type === "security" ? "What happened" : "Message"}
              hint="Blank line = new paragraph · start lines with “- ” for bullets or “1. ” for steps · **bold** · paste links as they are.">
              <textarea className="cv-input" rows={type === "informational" ? 8 : 5} style={area} value={form.details} onChange={(e) => set("details", e.target.value)} />
            </Field>

            {type === "feature" && (
              <div style={{ marginBottom: 14 }}>
                <span style={lbl}>Highlights</span>
                {form.highlights.map((h, i) => (
                  <div key={i} style={{ border: "1px solid var(--t-border-soft)", borderRadius: 12, padding: 12, marginBottom: 10, background: "var(--t-input)" }}>
                    <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                      <input className="cv-input" style={inp} placeholder={`Highlight ${i + 1} title`} value={h.title}
                        onChange={(e) => set("highlights", form.highlights.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} />
                      <button type="button" className="cv-btn cv-btn-icon cv-btn-ghost" aria-label="Remove this highlight" onClick={() => set("highlights", form.highlights.filter((_, j) => j !== i))}><Trash2 size={15} /></button>
                    </div>
                    <textarea className="cv-input" rows={3} style={area} placeholder="What it does and why it helps" value={h.text}
                      onChange={(e) => set("highlights", form.highlights.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
                    {images.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <Sel label="Screenshot for this highlight" value={h.image_id} onChange={(e) => set("highlights", form.highlights.map((x, j) => (j === i ? { ...x, image_id: e.target.value } : x)))}>
                          <option value="">No screenshot</option>
                          {images.map((im) => <option key={im.id} value={im.id}>{im.caption || im.name}</option>)}
                        </Sel>
                      </div>
                    )}
                  </div>
                ))}
                <button type="button" className="cv-btn" onClick={() => set("highlights", [...form.highlights, { title: "", text: "", image_id: "" }])}><Plus size={14} /> Add a highlight</button>
              </div>
            )}
            {type === "feature" && <Field label="How to get started (one step per line, optional)"><textarea className="cv-input" rows={3} style={area} value={form.steps} onChange={(e) => set("steps", e.target.value)} /></Field>}
            {type === "security" && <Field label="What we're doing (optional)"><textarea className="cv-input" rows={3} style={area} value={form.our_actions} onChange={(e) => set("our_actions", e.target.value)} /></Field>}
            {type === "maintenance" && <Field label="What people need to do (one step per line, optional)" hint="Leave empty and the email says there's nothing for them to do."><textarea className="cv-input" rows={3} style={area} value={form.actions} onChange={(e) => set("actions", e.target.value)} /></Field>}

            <div style={{ marginBottom: 14 }}>
              <span style={lbl}>Screenshots and pictures</span>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" multiple style={{ display: "none" }} onChange={(e) => { addImages(Array.from(e.target.files || [])); e.target.value = ""; }} />
              {images.map((im, i) => (
                <div key={im.id} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
                  <img src={im.url} alt="" style={{ width: 64, height: 42, objectFit: "cover", borderRadius: 6, border: "1px solid var(--t-border-soft)" }} />
                  <input className="cv-input" style={{ ...inp, flex: 1 }} placeholder="Caption (optional)" value={im.caption}
                    onChange={(e) => setImages((cur) => cur.map((x, j) => (j === i ? { ...x, caption: e.target.value } : x)))} />
                  <button type="button" className="cv-btn cv-btn-icon cv-btn-ghost" aria-label={`Remove ${im.name}`} onClick={() => setImages((cur) => cur.filter((_, j) => j !== i))}><Trash2 size={15} /></button>
                </div>
              ))}
              <button type="button" className="cv-btn" disabled={uploading} onClick={() => fileRef.current?.click()}><ImageIcon size={14} /> {uploading ? "Adding…" : "Add screenshots"}</button>
              <span style={{ display: "block", fontSize: 11.5, color: "var(--t-text-faint)", marginTop: 6 }}>Pictures go inside the email itself, so they show up without a “download pictures” prompt. Up to 5 MB each.</span>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 12 }}>
              <Field label="Button text (optional)"><input className="cv-input" style={inp} value={form.cta_label} onChange={(e) => set("cta_label", e.target.value)} placeholder="e.g. Open the portal" /></Field>
              <Field label="Button link"><input className="cv-input" style={inp} value={form.cta_url} onChange={(e) => set("cta_url", e.target.value)} placeholder="https://" /></Field>
            </div>
            <Field label={REASON_LABEL[type]}><textarea className="cv-input" rows={2} style={area} value={form.reason} onChange={(e) => set("reason", e.target.value)} /></Field>

            {preview.errors.length > 0 && <div style={{ fontSize: 12.5, color: "var(--badge-amber)", margin: "4px 0 12px" }}><AlertTriangle size={13} style={{ verticalAlign: -2 }} /> Still needed: {preview.errors.join(" ")}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button type="button" className="cv-btn cv-btn-primary" disabled={preview.errors.length > 0} onClick={goReview}>Check recipients <Send size={14} /></button>
            </div>
          </div>
          <div style={{ padding: 20, minWidth: 0, background: "var(--t-surface-deep)" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--t-text-label)", marginBottom: 8 }}>Live preview — exactly what people get</div>
            {preview.html
              ? <iframe title="Email preview" srcDoc={preview.html} sandbox="" style={{ width: "100%", height: 860, border: "1px solid var(--t-border-softer)", borderRadius: 12, background: "#eef2f7" }} />
              : <div style={{ color: "var(--t-text-faint)", fontSize: 13 }}>The preview appears as you type.</div>}
          </div>
          <style>{`@media (max-width: 1100px){ .me-grid{ grid-template-columns: 1fr !important; } }`}</style>
        </div>
      )}

      {step === 3 && people && (
        <div style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
            <strong style={{ fontSize: 15, color: "var(--t-text-primary)" }}>{chosen.size} {chosen.size === 1 ? "person" : "people"} will each get their own email</strong>
            <span style={{ fontSize: 12.5, color: "var(--t-text-faint)" }}>From MIDTC Support &lt;tickets@midtc.com&gt; · about {Math.max(1, Math.ceil(chosen.size / 24))} min to send · untick anyone who shouldn't get it</span>
          </div>
          <div style={{ border: "1px solid var(--t-border-soft)", borderRadius: 12, maxHeight: 480, overflowY: "auto", marginBottom: 16 }}>
            {[...(people.internal.length ? [{ client_name: "Internal staff", people: people.internal }] : []), ...people.groups].map((g) => {
              const real = g.people.filter((p) => !p.role), shared = g.people.filter((p) => p.role);
              const row = (p) => {
                const on = chosen.has(p.email);
                return (
                  <label key={p.email} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 14px 7px 30px", fontSize: 13, cursor: p.blocked ? "not-allowed" : "pointer", opacity: p.blocked ? 0.55 : 1 }}>
                    <input type="checkbox" disabled={p.blocked} checked={on} onChange={() => setChosen((s) => { const n = new Set(s); n.has(p.email) ? n.delete(p.email) : n.add(p.email); return n; })} />
                    <span style={{ fontWeight: 600, color: "var(--t-text-primary)" }}>{p.name}</span>
                    <span style={{ color: "var(--t-text-faint)" }}>{p.email}</span>
                    {p.blocked && <span style={pill("#64748b")}>blocked domain</span>}
                  </label>
                );
              };
              return (
                <div key={g.client_name} style={{ borderBottom: "1px solid var(--t-border-softer)", padding: "8px 0" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 14px" }}>
                    <strong style={{ fontSize: 13.5, color: "var(--t-text-primary)" }}>{g.client_name}</strong>
                    <span style={{ fontSize: 12, color: "var(--t-text-faint)" }}>{real.filter((p) => chosen.has(p.email)).length} of {real.length}</span>
                  </div>
                  {real.map(row)}
                  {shared.length > 0 && (
                    <details style={{ padding: "2px 14px" }}>
                      <summary style={{ fontSize: 12, color: "var(--t-text-muted)", cursor: "pointer", padding: "4px 0" }}>{shared.length} shared or system mailbox{shared.length === 1 ? "" : "es"} — not included unless ticked</summary>
                      {shared.map(row)}
                    </details>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <button type="button" className="cv-btn" onClick={sendTest} disabled={testState === "sending"}><FlaskConical size={14} /> {testState === "sending" ? "Sending a test…" : "Send me a test"}</button>
            {testState && testState !== "sending" && <span style={{ fontSize: 12.5, color: "var(--t-text-muted)" }}>{testState}</span>}
            <div style={{ flex: 1 }} />
            <button type="button" className="cv-btn cv-btn-primary" disabled={sending || !chosen.size} onClick={send}><Mail size={15} /> {sending ? "Queuing…" : `Send to ${chosen.size} ${chosen.size === 1 ? "person" : "people"}`}</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MassEmailPanel() {
  const [view, setView] = useState("log");
  const [justSent, setJustSent] = useState(null);
  return (
    <div className="cv-root">
      <ConvoStyles />
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <Mail size={18} style={{ color: "var(--t-accent)" }} />
        <h2 style={{ fontSize: 16, margin: 0 }}>Mass Email</h2>
        <span style={{ fontSize: 12.5, color: "var(--t-text-faint)" }}>Each person gets their own email from MIDTC Support — clients, MIDTC staff, or both.</span>
      </div>
      {view === "compose"
        ? <Compose onCancel={() => setView("log")} onQueued={(id) => { setJustSent(id); setView("log"); }} />
        : <Log onNew={() => setView("compose")} highlight={justSent} />}
    </div>
  );
}
